const { NumberFormatter } = require("@icabbi/hi-dash");
const i18n = require("@/plugins/i18n");


/**
 * Transforms various elements of the booking
 * for displaying in the UI.
 */
class BookingUiTransformer {

  /**
   * @param {string} defaultLocale
   * @param {string} currencySymbol
   * @param {number|null} fareEstimation
   * @param {Array<OfferAttributeExtraDto>} selectedAttributeExtras
   * @return {string}
   */
  static formatDisplayableFare({
    defaultLocale,
    currencySymbol,
    fareEstimation,
    selectedAttributeExtras,
  }) {
    const fareValue = BookingUiTransformer.formatFareValue({
      fareEstimation,
      defaultLocale,
      selectedAttributeExtras,
    });

    return `${currencySymbol} ${fareValue}`;
  }

  /**
   *
   * @param {string} defaultLocale
   * @param {string} currencySymbol
   * @param {number|null} fareEstimation
   * @param {Array<OfferAttributeExtraDto>|null} selectedAttributeExtras
   *
   * @return {string|null}
   */
  static formatFareValue({
    fareEstimation,
    defaultLocale,
    selectedAttributeExtras,
  }) {
    if (!fareEstimation) {
      return null;
    }

    const extraCost = BookingUiTransformer.calculateTotalExtraCost({ selectedAttributeExtras });

    if (fareEstimation.type === 'RANGE') {
      const minFare = NumberFormatter.stringValueDecimalSeparator(
        (fareEstimation.minimumFare + extraCost).toFixed(2),
        defaultLocale
      );

      const maxFare = NumberFormatter.stringValueDecimalSeparator(
        (fareEstimation.maximumFare + extraCost).toFixed(2),
        defaultLocale
      );

      return `${minFare} - ${maxFare}`;
    }

    if (fareEstimation.roundedPrice) {
      return NumberFormatter.stringValueDecimalSeparator(
        (fareEstimation.roundedPrice + extraCost).toFixed(2),
        defaultLocale
      );
    }

    return null;
  }

  /**
   * @param {string} currencySymbol
   * @param {Array<OfferAttributeExtraDto>} selectedAttributeExtras
   * @return {string}
   */
  static formatExtrasCost({
    currencySymbol,
    selectedAttributeExtras,
  }) {
    if (selectedAttributeExtras && selectedAttributeExtras.length > 0) {
      const totalCost = selectedAttributeExtras
        .map((e) => e.price || 0)
        .reduce((sum, val) => sum + val, 0);

      if (totalCost > 0) {
        return ` (+${currencySymbol}${totalCost})`;
      }
    }
    return '';
  }

  /**
   * @param {Array<OfferAttributeExtraDto>|null} selectedAttributeExtras
   * @return {number}
   */
  static calculateTotalExtraCost({
    selectedAttributeExtras,
  }) {
    if (selectedAttributeExtras && (selectedAttributeExtras.length > 0)) {
      return selectedAttributeExtras
        .map((e) => e.price || 0)
        .reduce((sum, val) => sum + val, 0);
    }
    return 0;
  }

  /**
   * @param {string} currencySymbol
   * @param {Array<OfferAttributeExtraDto>} selectedAttributeExtras
   * @return {string}
   */
  static formatSelectedAttributeExtrasForInlineDisplay({
    currencySymbol,
    selectedAttributeExtras,
  }) {
    if (!Array.isArray(selectedAttributeExtras) || (selectedAttributeExtras.length === 0)) {
      return '';
    }

    return selectedAttributeExtras
      .map((extra) => {
        return `${extra.displayName} (${currencySymbol}${extra.price})`;
      })
      .join(', ');
  }

  /**
   * @param {string} status
   * @return {string}
   */
  static convertStatusToDisplayFormat(status) {
    const stringKey = `entity.booking.statusCode.${status}`;

    if (i18n.vueI18n.te(stringKey)) {
      return i18n.vueI18n.t(stringKey).toUpperCase();
    }

    if (i18n.vueI18n.te(stringKey, 'en')) {
      return i18n.vueI18n.t(stringKey, "en").toUpperCase();
    }

    return status;
  }
}

module.exports = {
  BookingUiTransformer,
};
